<template>
  <div id="new">
    <pre v-if="debug">
      stage: {{ stage }}
      template: {{ template }}
      wizard: {{ wizard }}
      selfBook: {{ isSelfBook }}
      orgId: {{ organizationId }}
      customerId: {{ customerId }}
      userId: {{ userId }}
    </pre>
    <Result v-model:error="submitError" />
    <Guide v-show="stage == 'guide'" v-model:stage="stage" v-model:customer-id="customerId" v-model:customer-name="customerName" v-model:customer-email="customerEmail" v-model:organization-id="organizationId" v-model:identifier="identifier" v-model:template="template" v-model:wizard="wizard" @error="$emit('error', $event)" />
    <div v-show="stage == 'wizard'">
      <Header v-model:stage="stage" :wizard="wizard" />
      <WizardBook v-if="wizard == 'book' || wizard == 'assist'" ref="wizardBookRef" :key="wizard + organizationId" v-model:is-self-book="isSelfBook" :is-premium="template == 'Premium'" :organization-id="organizationId" :debug="debug" @create-trip="submit" @error="$emit('error', $event)" @scroll="$emit('scroll', $event)" />
      <WizardConcierge v-else :template="template" @create-trip="submit" />
    </div>
    <div v-show="stage == 'card'">
      <Card v-model:is-card="isCard" v-model:stage="stage" :organization-id="organizationId" @create-trip="resubmit" />
    </div>
    <div v-if="stage == 'confirm'">
      <Confirm :is-self-book="isSelfBook" :trip-id="tripId" />
    </div>
  </div>
</template>

<script setup>
import { inject, ref, watch } from "vue";
import useApi from "../utils/apiCalls.js";
import Header from "./TripNewGeneralHeader.vue";
import Card from "./TripNewCard.vue";
import Confirm from "./TripNewConfirm.vue";
import Guide from "./TripNewGuide.vue";
import Result from "./common/FormResult.vue";
import WizardBook from "./TripNewBook.vue";
import WizardConcierge from "./TripNewConcierge.vue";

const userId = inject("userId");

const emit = defineEmits(["error", "scroll"]);
const { apiGetRequest, apiPostRequest } = useApi(emit);

document.title = "Flightfox - New Trip";

const stage = ref("guide");
const customerId = ref(null);
const customerName = ref(null);
const customerEmail = ref(null);
const organizationId = ref(null);
const identifier = ref(null);
const isSelfBook = ref(false);
const template = ref(null);
const tripId = ref(null);
const wizard = ref(null);

const debug = ref(false);

//dev shortcut :)
//stage.value = "wizard";
//wizard.value = "book";
//template.value = "Standard";

watch(
  () => isSelfBook.value,
  val => {
    if (val === false && wizard.value == "book") {
      wizard.value = "assist";
    }
  }
);
watch(
  () => wizard.value,
  val => {
    if (val == "book") {
      isSelfBook.value = true;
    } else {
      isSelfBook.value = false;
    }
  }
);

const submitError = ref(null);
const wizardBookRef = ref(null);

const isCard = ref(false);
const loadIsCard = () => {
  apiGetRequest({
    path: "/is-card",
    onSuccess: data => {
      isCard.value = data;
    },
  });
};
loadIsCard();

var tripCache;
const submit = t => {
  // Show card form if necessary
  if (!isCard.value) {
    tripCache = t;
    stage.value = "card";
    return;
  }

  apiPostRequest({
    path: "/trips",
    data: {
      customer_id: customerId.value ? customerId.value : userId.value,
      customer_name: customerName.value,
      customer_email: customerEmail.value,
      organization_id: organizationId.value,
      trip_type: template.value,
      identifier: identifier.value,
      description: t.description,
      destination: t.destination,
      date_start: t.dateStart,
      date_end: t.dateEnd,
      is_self_book: t.isSelfBook,
      flights: t.flights,
      properties: t.properties,
      travelers: t.travelers,
      self_book_allowed: isSelfBook.value,
      self_book_allowed_reason: isSelfBook.value ? null : (wizard.value === "concierge" ? "chose_concierge" : "chose_assisted"),
      self_book_concierge_chosen: wizard.value === "concierge",
      self_book_flight_results_count: 0,
    },
    onSuccess: data => {
      submitError.value = null;
      tripId.value = data.id;
      stage.value = "confirm";
    },
    onError: err => {
      wizardBookRef.value?.resetLoading();
      const msg = err?.message || "";
      if (msg.includes("no longer available") || msg.includes("price")) {
        wizardBookRef.value?.resetFlights();
      }
      submitError.value = err;
    },
  });
};
const resubmit = () => {
  submit(tripCache);
};
</script>

<style lang="less">
#new .summary .item.flight {
  background: inherit !important;
}

#new {
  .summary {
    textarea {
      height: 5rem;
      margin-bottom: 0;
      resize: vertical;
    }

    .item {
      align-items: center;
      background: @light;
      margin-top: 1rem;
      padding: 1rem;

      .line {
        display: flex;

        span:first-child {
          flex: 1;
        }
      }

      .title {
        border-bottom: 0.0625rem solid @white;
        margin-bottom: 1rem;
        padding-bottom: 1rem;
      }
    }
  }
}

@media screen and (min-width: 60em) {
  #new {
    .summary {
      .form-section:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>
